/**
 * Per-section Action History — wire types + the pure tree builder.
 *
 * Shared by the Word add-in (`ActionHistoryTab`) and the web app
 * (`ActionHistoryScreen`) so both render the same hierarchy from the same
 * `GET /runs/{id}/sections/{mid}/action-history` payload:
 *
 *   Superintendent → Lead planner → Layer n → node «title» → tool calls
 *   Drafting agent → tool calls → reasoning → draft
 *   Resolve issues pass · requested by X → tool calls
 *   Edit approved by X / Manual save by X / Proposal accepted by X …
 *
 * Pure and dependency-free: node-testable, no React, no fetch.
 */

/** One structured literature result from a `search_literature` call. */
export interface WebResult {
  source: string // "pubmed" | "clinical_trials" | "biorxiv" | "chembl"
  title: string
  year: number | null
  journal: string | null
  url: string | null
  pmid: string | null
  nct_id: string | null
  doi: string | null
  abstract: string
  study_type?: string | null
  primary_endpoints?: string | null
  has_results?: boolean | null
  results_summary?: string | null
}

/** Fields common to every context-preparation tool call. */
export interface ActionCallBase {
  tool_name: string
  transaction_id: string | null
  input_params: Record<string, unknown>
  outcome: 'success' | 'failure' | 'written_off' | 'partial'
  result_length: number
  duration_ms: number | null
  started_at: string | null
  // Agent-span identity for the hierarchy. Null on flat / legacy runs.
  span_id?: string | null
  parent_span_id?: string | null
  node_id?: string | null
  agent_role?: string | null // superintendent | lead | child | main | retrieval | resolve
  // Human-readable label of the sub-agent that made this call (e.g. a resolve
  // child's target issue marker). Labels the agent node it hangs under.
  span_title?: string | null
}

export interface InternetCall extends ActionCallBase {
  query: string
  web_total: number | null
  web_sources: string[]
  web_results: WebResult[]
  goal?: string
  web_summary?: string
  web_sub_queries?: string[]
  web_used_refs?: Array<Record<string, unknown>>
}

export interface LibraryCall extends ActionCallBase {
  cascade_pass: number | null
  source_detail: {
    source_ref: Record<string, unknown>
    section_number: string | null
    snippet: string
    content_length: number
  } | null
}

/** One node of the superintendent plan skeleton (empty for the flat path). */
export interface PlanNode {
  node_id: string
  target_section_id: string
  title: string
  depends_on: string[]
  layer: number
}

/** One agent / sub-agent span seen on the section (server-synthesized parents included). */
export interface AgentSpanNode {
  span_id: string
  parent_span_id: string | null
  role: string
  node_id: string | null
  title: string | null
  target_section_id: string | null
}

/** An attributable act against the section — approvals, saves, resolve passes, drafting authority. */
export interface ActivityEvent {
  kind: 'activity'
  started_at: string | null
  action: string
  actor: string
  actor_id: string | null
  actor_type: string // human | service | ai_agent | import | inspector
  authority_actor_id: string | null
  outcome: string // applied | refused | conflict | skipped | error
  section_version: number | null
  detail: Record<string, unknown>
  /** The span root the act's tool calls hang off (a resolve pass, an approved rewrite). */
  span_id: string | null
}

/** A non-retrieval tool call — terminal submit_* or any other tool. */
export interface OtherCall extends ActionCallBase {
  phase?: string | null
}

export type RetrievalEvent = LibraryCall & { kind: 'retrieval'; count: number; total_duration_ms: number | null }
export type InternetEvent = InternetCall & { kind: 'internet'; count: number; total_duration_ms: number | null }
export type GenericToolEvent = OtherCall & { kind: 'tool'; count: number; total_duration_ms: number | null }
export type ReasoningEvent = {
  kind: 'reasoning'
  started_at: string | null
  thinking_tokens: number
  traces: string[]
  traces_available: boolean
}
export type DraftEvent = {
  kind: 'draft'
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  model: string
  input_tokens: number
  output_tokens: number
  thinking_tokens: number
  draft_length: number
}

export type TimelineEvent =
  | RetrievalEvent
  | InternetEvent
  | GenericToolEvent
  | ReasoningEvent
  | DraftEvent
  | ActivityEvent

/** One selectable attempt of a section's agent log (archived regens + the live one). */
export interface AttemptInfo {
  attempt_number: number
  archived_at: string | null
  final_spine_status: string | null
  guidance: string | null
  is_current: boolean
}

export interface ActionHistoryPayload {
  module_id: string
  available: boolean
  internet_calls: InternetCall[]
  library_calls: LibraryCall[]
  other_calls?: OtherCall[]
  timeline?: TimelineEvent[]
  plan_nodes?: PlanNode[]
  plan_synthesized?: boolean
  has_hierarchy?: boolean
  agents?: AgentSpanNode[]
  attempts?: AttemptInfo[]
  attempt?: number
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

export type ActionTreeKind = 'agent' | 'layer' | 'activity' | 'leaf' | 'note'

export interface ActionTreeNode {
  key: string
  kind: ActionTreeKind
  icon: string
  label: string
  sub?: string
  /** ISO time used for ordering (earliest descendant for branches). */
  at: string | null
  children: ActionTreeNode[]
  /** The timeline event a leaf (or an activity node) renders. */
  event?: TimelineEvent
  /** Agent role for `agent` nodes; outcome / actor type for `activity` nodes. */
  role?: string
  outcome?: string
  actorType?: string
}

const ROLE_ICON: Record<string, string> = {
  superintendent: '🧭',
  lead: '🧠',
  child: '📄',
  main: '✍️',
  resolve: '🩹',
  retrieval: '📚',
  cascade: '📚',
}

const KIND_ICON: Record<TimelineEvent['kind'], string> = {
  retrieval: '📚',
  internet: '🌐',
  tool: '🔧',
  reasoning: '🧠',
  draft: '✍️',
  activity: '👤',
}

// Terminal / control tools get a distinct glyph from generic tools so the
// submitted draft/rewrite reads as the agent's output, not another fetch.
const TOOL_ICON: Record<string, string> = {
  submit_draft: '📝',
  submit_rewrite: '📝',
  submit_plan: '🗂️',
}

/** Friendly label for a terminal/other tool leaf. */
export function toolLeafLabel(toolName: string): string {
  switch (toolName) {
    case 'submit_draft':
      return 'Submitted draft'
    case 'submit_rewrite':
      return 'Submitted rewrite'
    case 'submit_plan':
      return 'Submitted plan'
    default:
      return toolName
  }
}

// Fallback labels for agent nodes with no span title, by role.
const AGENT_ROLE_LABEL: Record<string, string> = {
  superintendent: 'Superintendent',
  lead: 'Lead planner',
  child: 'Sub-agent',
  main: 'Agent',
  resolve: 'Resolve pass',
  retrieval: 'Retrieval sub-agent',
  cascade: 'Retrieval sub-agent',
}

const ACTOR_ICON: Record<string, string> = {
  human: '👤',
  ai_agent: '🤖',
  service: '⚙️',
  import: '📥',
  inspector: '🔍',
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}
function clip(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s
}
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** Human label (+ optional sub-line) for one activity event. */
export function activityLabel(ev: ActivityEvent): { label: string; sub?: string } {
  const d = ev.detail ?? {}
  const actor = ev.actor || 'unknown'
  const version = ev.section_version != null ? `v${ev.section_version}` : undefined
  switch (ev.action) {
    case 'section.resolve_issues': {
      const requestedBy = str(d.requested_by) ?? actor
      const filled = num(d.filled)
      const remaining = num(d.remaining)
      const counts =
        filled != null && remaining != null ? ` · ${filled} filled, ${remaining} left` : ''
      if (ev.outcome === 'skipped') {
        const mode = d.mode === 'pause' ? 'paused' : 'stopped'
        return { label: `Resolve issues pass ${mode} · requested by ${requestedBy}`, sub: version }
      }
      if (ev.outcome === 'conflict') {
        return { label: `Resolve issues pass skipped · section changed underneath · requested by ${requestedBy}` }
      }
      if (ev.outcome === 'error') {
        return { label: `Resolve issues pass failed · requested by ${requestedBy}` }
      }
      return { label: `Resolve issues pass · requested by ${requestedBy}${counts}`, sub: version }
    }
    case 'section.edit_applied': {
      const kind = str(d.edit_kind) ?? (d.source === 'agent_redraft' ? 'approved_proposal' : 'manual_save')
      const verb =
        kind === 'approved_proposal'
          ? "Approved Asthra's rewrite"
          : kind === 'bulk_save'
            ? 'Bulk save'
            : 'Manual save'
      // The chat edit-highlight trigger — a short label; the user's instruction
      // (and the text they highlighted) heads the edit's subtree as a note child
      // (see buildActionTree), rather than being crammed into this row.
      const instruction = str(d.instruction)
      if (instruction) return { label: 'Edit' }
      // The actor is already shown as a chip on the row, so no `· ${actor}` here.
      return { label: verb, sub: version }
    }
    case 'section.adhoc_decision': {
      const decision = str(d.decision) ?? str(d.status) ?? str(d.outcome) ?? 'decided'
      return { label: `Proposal ${decision} · ${actor}`, sub: version }
    }
    case 'section.transition': {
      const to = str(d.to) ?? str(d.to_status) ?? str(d.status)
      return { label: to ? `Marked ${to} · ${actor}` : `Section transition · ${actor}`, sub: version }
    }
    case 'section.agent_draft_authority':
      // The actor ("Asthra") is already the row's chip, so just "Drafted".
      return {
        label: 'Drafted',
        sub: ev.authority_actor_id ? `authority: ${ev.authority_actor_id}` : undefined,
      }
    case 'section.audit_annotate':
      return { label: `Regulatory audit annotated · ${actor}`, sub: version }
    case 'section.regen_reset': {
      const guidance = str(d.guidance)
      const requestedBy = str(d.requested_by) ?? actor
      if (guidance) return { label: `Regenerated · ${requestedBy}`, sub: `“${clip(guidance, 80)}”` }
      return { label: `Regenerated · ${requestedBy}`, sub: version }
    }
    case 'section.edit_legacy':
      return { label: `${actor} edit`, sub: version }
    default:
      return { label: `${ev.action} · ${actor}`, sub: version }
  }
}

type ToolEvent = RetrievalEvent | InternetEvent | GenericToolEvent

function isTool(ev: TimelineEvent): ev is ToolEvent {
  return ev.kind === 'retrieval' || ev.kind === 'internet' || ev.kind === 'tool'
}

function byAt(a: ActionTreeNode, b: ActionTreeNode): number {
  if (a.at === b.at) return 0
  if (a.at === null) return 1
  if (b.at === null) return -1
  return a.at < b.at ? -1 : 1
}

function earliest(nodes: ActionTreeNode[]): string | null {
  let best: string | null = null
  for (const n of nodes) {
    if (n.at !== null && (best === null || n.at < best)) best = n.at
  }
  return best
}

function leaf(ev: TimelineEvent, key: string): ActionTreeNode {
  let label: string
  let icon = KIND_ICON[ev.kind]
  if (ev.kind === 'reasoning') label = 'Reasoning'
  else if (ev.kind === 'draft') label = 'Drafted section'
  else if (ev.kind === 'activity') label = activityLabel(ev).label
  else if (ev.kind === 'tool') {
    label = toolLeafLabel(ev.tool_name)
    icon = TOOL_ICON[ev.tool_name] ?? KIND_ICON.tool
  } else label = ev.tool_name
  return { key, kind: 'leaf', icon, label, at: ev.started_at, children: [], event: ev }
}

function agentNode(key: string, role: string, label: string, children: ActionTreeNode[], sub?: string): ActionTreeNode {
  children.sort(byAt)
  return {
    key,
    kind: 'agent',
    icon: ROLE_ICON[role] ?? '•',
    label,
    sub,
    at: earliest(children),
    children,
    role,
  }
}

function activityNode(ev: ActivityEvent, key: string, children: ActionTreeNode[]): ActionTreeNode {
  const { label, sub } = activityLabel(ev)
  children.sort(byAt)
  return {
    key,
    kind: 'activity',
    icon: ACTOR_ICON[ev.actor_type] ?? '👤',
    label,
    sub,
    at: ev.started_at,
    children,
    event: ev,
    outcome: ev.outcome,
    actorType: ev.actor_type,
  }
}

function callsLabel(n: number): string {
  return `${n} call${n === 1 ? '' : 's'}`
}

/**
 * Build the hierarchy from a payload. Deterministic: the same payload always
 * yields the same keys, so open/closed state keyed on them survives a refetch.
 */
export function buildActionTree(payload: ActionHistoryPayload): ActionTreeNode[] {
  const timeline = payload.timeline ?? []
  const agents = new Map<string, AgentSpanNode>()
  for (const a of payload.agents ?? []) agents.set(a.span_id, a)
  // Spans seen on tool events but missing from the index (older servers).
  for (const ev of timeline) {
    if (isTool(ev) && ev.span_id && !agents.has(ev.span_id)) {
      agents.set(ev.span_id, {
        span_id: ev.span_id,
        parent_span_id: ev.parent_span_id ?? null,
        role: ev.agent_role ?? 'main',
        node_id: ev.node_id ?? null,
        title: ev.span_title ?? null,
        target_section_id: null,
      })
    }
  }

  const rootOf = (spanId: string): string => {
    let cur = spanId
    const guard = new Set<string>()
    for (;;) {
      const a = agents.get(cur)
      if (!a || !a.parent_span_id || guard.has(cur)) return cur
      guard.add(cur)
      cur = a.parent_span_id
    }
  }
  const chainHas = (spanId: string, pred: (a: AgentSpanNode) => boolean): boolean => {
    let cur: string | null = spanId
    const guard = new Set<string>()
    while (cur && !guard.has(cur)) {
      guard.add(cur)
      const a = agents.get(cur)
      if (!a) return false
      if (pred(a)) return true
      cur = a.parent_span_id
    }
    return false
  }

  const activities = timeline.filter((e): e is ActivityEvent => e.kind === 'activity')
  const activityBySpan = new Map<string, ActivityEvent>()
  for (const a of activities) if (a.span_id) activityBySpan.set(a.span_id, a)

  // Bucket tool events.
  // Leaves adopted by an activity root, keyed by their EXACT span so the pass's
  // sub-agents can be rebuilt as nested nodes (each labeled by its span title —
  // e.g. a resolve child's target marker), not flattened onto the pass.
  const activityLeavesBySpan = new Map<string, ActionTreeNode[]>() // exact span_id → leaves
  const superLead: ActionTreeNode[] = []
  const superByNode = new Map<string, ActionTreeNode[]>()
  const superOther: ActionTreeNode[] = []
  const mainLeaves: ActionTreeNode[] = []
  const orphans: ActionTreeNode[] = []
  let sawSuper = false

  timeline.forEach((ev, i) => {
    if (!isTool(ev)) return
    const node = leaf(ev, `tl-${i}`)
    const spanId = ev.span_id ?? null
    if (!spanId) {
      if (ev.node_id) {
        sawSuper = true
        const arr = superByNode.get(ev.node_id) ?? []
        arr.push(node)
        superByNode.set(ev.node_id, arr)
      } else if (ev.agent_role === 'lead') {
        sawSuper = true
        superLead.push(node)
      } else {
        orphans.push(node)
      }
      return
    }
    const root = rootOf(spanId)
    const act = activityBySpan.get(root) ?? (chainHas(spanId, (a) => activityBySpan.has(a.span_id)) ? undefined : undefined)
    // Any span in the chain that is an activity root adopts the leaf.
    let adopted: string | null = null
    let cur: string | null = spanId
    const guard = new Set<string>()
    while (cur && !guard.has(cur)) {
      guard.add(cur)
      if (activityBySpan.has(cur)) {
        adopted = cur
        break
      }
      cur = agents.get(cur)?.parent_span_id ?? null
    }
    void act
    if (adopted) {
      const arr = activityLeavesBySpan.get(spanId) ?? []
      arr.push(node)
      activityLeavesBySpan.set(spanId, arr)
      return
    }
    const rootAgent = agents.get(root)
    const isSuper =
      (rootAgent?.role === 'superintendent') ||
      chainHas(spanId, (a) => a.role === 'lead' || a.role === 'child' || a.role === 'superintendent')
    if (isSuper) {
      sawSuper = true
      const selfAgent = agents.get(spanId)
      if (chainHas(spanId, (a) => a.role === 'lead') && !ev.node_id) {
        superLead.push(node)
      } else {
        const nodeId = ev.node_id ?? selfAgent?.node_id ?? null
        if (nodeId) {
          const arr = superByNode.get(nodeId) ?? []
          arr.push(node)
          superByNode.set(nodeId, arr)
        } else {
          superOther.push(node)
        }
      }
      return
    }
    mainLeaves.push(node)
  })

  const roots: ActionTreeNode[] = []

  // Superintendent tree.
  if (sawSuper || (payload.plan_nodes?.length ?? 0) > 0) {
    const planNodes = payload.plan_nodes ?? []
    const layers = new Map<number, PlanNode[]>()
    for (const n of planNodes) {
      const arr = layers.get(n.layer) ?? []
      arr.push(n)
      layers.set(n.layer, arr)
    }
    const known = new Set(planNodes.map((n) => n.node_id))
    const children: ActionTreeNode[] = []
    children.push(agentNode('lead', 'lead', 'Lead planner', superLead, callsLabel(superLead.length)))
    for (const lk of Array.from(layers.keys()).sort((a, b) => a - b)) {
      const layerChildren = (layers.get(lk) ?? []).map((n) => {
        const calls = superByNode.get(n.node_id) ?? []
        return agentNode(
          `node:${n.node_id}`,
          'child',
          n.title || n.node_id,
          calls,
          `→ §${n.target_section_id} · ${callsLabel(calls.length)}`,
        )
      })
      children.push({
        key: `layer:${lk}`,
        kind: 'layer',
        icon: '',
        label: `Layer ${lk}`,
        at: earliest(layerChildren),
        children: layerChildren,
      })
    }
    // Nodes seen on calls but absent from the plan skeleton.
    for (const [nodeId, calls] of superByNode) {
      if (known.has(nodeId)) continue
      children.push(agentNode(`node:${nodeId}`, 'child', nodeId, calls, callsLabel(calls.length)))
    }
    if (superOther.length > 0) children.push(agentNode('super-other', 'main', 'Other calls', superOther))
    const total = superLead.length + superOther.length + Array.from(superByNode.values()).reduce((s, a) => s + a.length, 0)
    const sub = `${planNodes.length} node${planNodes.length === 1 ? '' : 's'} · ${layers.size} layer${layers.size === 1 ? '' : 's'} · ${callsLabel(total)}${payload.plan_synthesized ? ' · synthesized plan' : ''}`
    const superNode = agentNode('super', 'superintendent', 'Superintendent', children, sub)
    // Layers keep plan order, not time order.
    superNode.children = children
    roots.push(superNode)
  }

  // Drafting agent (flat path) — its tool calls, then reasoning, then draft.
  const draftingChildren = [...mainLeaves]
  timeline.forEach((ev, i) => {
    if (ev.kind === 'reasoning' || ev.kind === 'draft') draftingChildren.push(leaf(ev, `tl-${i}`))
  })
  if (draftingChildren.length > 0) {
    // Reasoning/draft without timestamps must still trail the tool calls.
    const timed = draftingChildren.filter((c) => c.at !== null).sort(byAt)
    const untimed = draftingChildren.filter((c) => c.at === null)
    const node = agentNode('main', 'main', 'Drafting agent', [], callsLabel(mainLeaves.length))
    node.children = [...timed, ...untimed]
    node.at = earliest(node.children)
    // A drafting root that only holds reasoning/draft on a superintendent run
    // is that run's own tail — keep it under the superintendent instead.
    if (mainLeaves.length === 0 && roots.length > 0 && roots[0].role === 'superintendent') {
      roots[0].children.push(...node.children)
    } else {
      roots.push(node)
    }
  }

  // Activities (chronological). Each adopts its work as a NESTED sub-agent tree:
  // the pass root's direct child spans become their own agent nodes (labeled by
  // span title — a resolve child's target marker), each carrying its own tool
  // calls and any deeper retrieval sub-agents. Leaves recorded directly on the
  // pass root attach to it.
  const childSpansOf = (spanId: string): AgentSpanNode[] => {
    const out: AgentSpanNode[] = []
    for (const a of agents.values()) if (a.parent_span_id === spanId) out.push(a)
    return out
  }
  const subtreeForSpan = (spanId: string, guard: Set<string>): ActionTreeNode => {
    const a = agents.get(spanId)
    const own = activityLeavesBySpan.get(spanId) ?? []
    const kids: ActionTreeNode[] = []
    if (!guard.has(spanId)) {
      guard.add(spanId)
      for (const c of childSpansOf(spanId)) kids.push(subtreeForSpan(c.span_id, guard))
    }
    const role = a?.role ?? 'main'
    const label = a?.title || AGENT_ROLE_LABEL[role] || 'Sub-agent'
    const n = agentNode(`span:${spanId}`, role, label, [...own, ...kids])
    n.sub = callsLabel(countLeaves(n))
    return n
  }
  const activityChildren = (rootSpanId: string): ActionTreeNode[] => {
    const own = activityLeavesBySpan.get(rootSpanId) ?? []
    const guard = new Set<string>([rootSpanId])
    const kids = childSpansOf(rootSpanId).map((c) => subtreeForSpan(c.span_id, guard))
    return [...own, ...kids]
  }
  activities.forEach((ev, i) => {
    const key = `act-${ev.span_id ?? i}-${ev.started_at ?? i}`
    const children = ev.span_id ? activityChildren(ev.span_id) : []
    const n = activityNode(ev, key, children)
    // A chat edit-highlight carries the user's instruction (and the text they
    // highlighted). Lead the edit's subtree with it as a note, so the row label
    // stays short and the full input reads inside on expand. Unshift, because
    // activityNode has already time-sorted the children and the input must lead.
    const instruction = ev.action === 'section.edit_applied' ? str(ev.detail?.instruction) : null
    if (instruction) {
      const selected = str(ev.detail?.selected_text)
      n.children.unshift({
        key: `${key}-input`,
        kind: 'note',
        icon: '',
        label: instruction,
        sub: selected ? `on “${clip(selected, 200)}”` : undefined,
        at: ev.started_at,
        children: [],
      })
    }
    roots.push(n)
  })

  if (orphans.length > 0) roots.push(agentNode('orphan', 'retrieval', 'Other calls', orphans, callsLabel(orphans.length)))

  return roots.sort(byAt)
}

/** Flat count of tool-call leaves under a node (for badges). */
export function countLeaves(node: ActionTreeNode): number {
  if (node.kind === 'leaf') return node.event && isTool(node.event) ? 1 : 0
  return node.children.reduce((s, c) => s + countLeaves(c), 0)
}
